import { useEffect, useState } from "react";
import { X, Star } from "lucide-react";

const STORAGE_KEY = "atr_review_nudge_dismissed_at";

const DEFAULTS = {
  enabled: true,
  delay_seconds: 30,
  cooldown_days: 30,
  title: "Content de nos services ?",
  subtitle: "Un avis Google nous ferait vraiment plaisir — ça prend 30 secondes et ça aide énormément l'atelier 🙏",
  cta_text: "Laisser un avis",
  later_text: "Plus tard",
};

/**
 * Non-intrusive nudge asking happy visitors to leave a Google review.
 * All copy + timings are editable from the admin via /api/content/review_nudge.
 */
export const ReviewNudgeToast = ({ placeId, businessUrl, config }) => {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const cfg = { ...DEFAULTS, ...(config || {}) };
  const enabled = cfg.enabled !== false;
  const delayMs = Math.max(0, (cfg.delay_seconds ?? DEFAULTS.delay_seconds) * 1000);
  const cooldownMs = Math.max(0, (cfg.cooldown_days ?? DEFAULTS.cooldown_days) * 24 * 60 * 60 * 1000);

  useEffect(() => {
    if (!enabled) return undefined;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw && Date.now() - parseInt(raw, 10) < cooldownMs) return undefined;
    } catch {
      /* localStorage disabled — proceed anyway */
    }

    const t = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(t);
  }, [enabled, delayMs, cooldownMs]);

  const dismiss = () => {
    setLeaving(true);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    setTimeout(() => setVisible(false), 300);
  };

  const openReviewForm = () => {
    const reviewUrl = placeId
      ? `https://search.google.com/local/writereview?placeid=${placeId}`
      : (businessUrl || "https://www.google.com/maps/search/Auvergne+Tech+Racing+Clermont-Ferrand");
    window.open(reviewUrl, "_blank", "noopener,noreferrer");
    dismiss();
  };

  if (!enabled || !visible) return null;

  return (
    <div
      data-testid="review-nudge-toast"
      className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60] max-w-[92vw] sm:max-w-sm
                  transition-all duration-300 ease-out
                  ${leaving ? "translate-x-8 opacity-0" : "translate-x-0 opacity-100"}`}
      style={{ animation: leaving ? undefined : "slideInReview 400ms cubic-bezier(0.16, 1, 0.3, 1)" }}
    >
      <style>{`
        @keyframes slideInReview {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      <div className="relative bg-[#121212]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#FF3B30] via-[#FF6B62] to-[#FF3B30]" />

        <button
          onClick={dismiss}
          data-testid="review-nudge-close"
          aria-label="Fermer"
          className="absolute top-3 right-3 text-white/40 hover:text-white/80 transition-colors"
        >
          <X size={16} />
        </button>

        <div className="p-5 pt-6">
          <div className="flex items-center gap-3 mb-3">
            <svg viewBox="0 0 24 24" className="w-6 h-6 flex-shrink-0" aria-label="Google">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
              ))}
            </div>
          </div>

          <h4 className="text-white font-bold text-base leading-snug mb-1">{cfg.title}</h4>
          <p className="text-white/60 text-sm leading-relaxed mb-4 whitespace-pre-line">{cfg.subtitle}</p>

          <div className="flex items-center gap-2">
            <button
              onClick={openReviewForm}
              data-testid="review-nudge-cta"
              className="flex-1 bg-[#FF3B30] hover:bg-[#D63025] text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors uppercase tracking-wide"
            >
              {cfg.cta_text}
            </button>
            <button
              onClick={dismiss}
              data-testid="review-nudge-later"
              className="text-white/40 hover:text-white/70 text-sm px-3 py-2.5 transition-colors"
            >
              {cfg.later_text}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
